import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  DetailPanelGrid,
  useBackofficePageMode,
  useCustomizableDetailTabs,
} from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import { fetchMessageBusTopic } from './api';
import { MessageBusBarTile, MessageBusPageHeader, MessageBusSummaryTile } from './components';
import type { BackofficeMessageBusTopicDetail } from './types';

const TABS: Array<{ id: 'topic'; label: string }> = [{ id: 'topic', label: 'Topic' }];
const TABS_PREFERENCE_SCOPE = 'messagebus:topic:tabs';
const LAYOUT_PREFERENCE_SCOPE = 'messagebus:topic:layout';
const DEFAULT_TILE_IDS = ['topic-summary', 'subscribers'];

export function BackofficeMessageBusTopicPage() {
  const { topicName = '' } = useParams<{ topicName: string }>();
  const decodedTopicName = decodeURIComponent(topicName);
  useBackofficePageTitle(`Message Bus Topic: ${decodedTopicName}`);
  const { pageMode, setPageMode } = useBackofficePageMode();
  const [data, setData] = useState<BackofficeMessageBusTopicDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const tabs = useCustomizableDetailTabs<'topic'>({
    preferenceScope: TABS_PREFERENCE_SCOPE,
    builtInTabs: TABS,
    defaultTabId: 'topic',
    basePath: `/developer/messagebus/topics/${encodeURIComponent(decodedTopicName)}`,
    pageMode,
  });
  const activeCustomTab = tabs.activeCustomTab;
  const layoutPreferenceScope = activeCustomTab
    ? `${LAYOUT_PREFERENCE_SCOPE}:${activeCustomTab.id}`
    : LAYOUT_PREFERENCE_SCOPE;
  const defaultVisibleTileIds = activeCustomTab ? [] : DEFAULT_TILE_IDS;
  const showTabs = pageMode === 'edit' || tabs.customTabs.length > 0;

  const load = useCallback(
    (signal?: AbortSignal, forceRefresh = false) => {
      setLoading(true);
      setError(null);
      return fetchMessageBusTopic(decodedTopicName, signal, forceRefresh)
        .then(setData)
        .catch((caught: unknown) => {
          if (!(caught instanceof DOMException && caught.name === 'AbortError')) {
            setError(caught instanceof Error ? caught.message : 'Failed to load topic');
          }
        })
        .finally(() => {
          if (!signal?.aborted) {
            setLoading(false);
          }
        });
    },
    [decodedTopicName]
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const refresh = useCallback(() => {
    setRefreshing(true);
    void load(undefined, true).finally(() => setRefreshing(false));
  }, [load]);

  const items = useMemo(
    () => [
      {
        id: 'topic-summary',
        title: 'Topic summary',
        layout: { w: 4, h: 3, minW: 2, minH: 2, maxW: 6, maxH: 4, isResizable: true },
        render: () =>
          data ? (
            <MessageBusSummaryTile
              title="Topic summary"
              panelId="topic-summary"
              rows={[
                { label: 'Topic', value: data.topic.name },
                { label: 'Subscribers', value: String(data.topic.subscriberCount) },
                { label: 'Current messages', value: String(data.topic.currentMessageCount) },
                { label: 'Active messages', value: String(data.topic.activeMessageCount) },
                { label: 'Dead letters', value: String(data.topic.deadLetterMessageCount) },
                { label: 'Scheduled', value: String(data.topic.scheduledMessageCount) },
                {
                  label: 'Size',
                  value: data.topic.sizeInBytes === null ? '—' : `${data.topic.sizeInBytes} bytes`,
                },
              ]}
            />
          ) : null,
      },
      {
        id: 'subscribers',
        title: 'Subscribers',
        layout: { w: 8, h: 3, minW: 4, minH: 2, maxW: 12, maxH: 5, isResizable: true },
        render: () =>
          data ? (
            <MessageBusBarTile
              title="Subscribers"
              panelId="subscribers"
              rows={data.subscribers.map((subscriber) => ({
                ...subscriber,
                topicHref: `/developer/messagebus/topics/${encodeURIComponent(subscriber.topicName)}`,
                subscriberHref: `/developer/messagebus/topics/${encodeURIComponent(subscriber.topicName)}/subscribers/${encodeURIComponent(subscriber.subscriberName)}`,
              }))}
              emptyLabel="No subscribers found."
            />
          ) : null,
      },
    ],
    [data]
  );

  return (
    <main className="min-h-[320px] bg-white p-6 md:p-8" aria-label="Backoffice main content">
      <div className="flex flex-col gap-5">
        {/* extracted to keep message-bus page actions aligned across overview, topic, and subscriber */}
        <MessageBusPageHeader
          showTabs={showTabs}
          tabs={tabs}
          pageLabel="topic page"
          pageMode={pageMode}
          onPageModeChange={setPageMode}
          onRefresh={refresh}
          refreshing={refreshing}
        />
        {loading && !data ? <p className="text-body text-neutral-500">Loading topic…</p> : null}
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-body text-red-700">
            {error}
          </div>
        ) : null}
        {data ? (
          <DetailPanelGrid
            resetKey={tabs.activeTabId}
            preferenceScope={layoutPreferenceScope}
            layoutMode={pageMode}
            cols={{ xxl: 12, lg: 12, md: 6, sm: 4, xs: 1, xxs: 1 }}
            defaultVisibleItemIds={defaultVisibleTileIds}
            addTileLayout={{ w: 2, h: 1 }}
            tileCatalogLabel="Add topic tile"
            items={items}
          />
        ) : null}
      </div>
    </main>
  );
}
